/**
 * src/marketing/content.ts - single source of truth for the getabeam.com MARKETING SITE copy + config.
 *
 * This is the public marketing/website surface for the BEAM PRODUCT ITSELF (getabeam.com:
 * marketing, account creation, upgrades, app downloads, and the link to the web-app login).
 * It is deliberately SEPARATE from any per-tenant company website that links a customer's own
 * site to their BEAM org (see /WEBSITE-BUILD-PLAN.md) - do not conflate the two.
 *
 * Web-only surface. Everything a non-engineer would want to tweak (headlines, feature copy,
 * the price shown, store/download links) lives here so the page components stay structural.
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */

export const SITE = {
  domain: 'getabeam.com',
  product: 'Beam',
  vendor: 'CompVer Consulting',
  tagline: 'Run the whole job from one app.',
  // One-liner used in the hero sub + meta description.
  blurb:
    'Beam is the all-in-one operating system for trades and contractors: estimates, invoices, scheduling, permits, payments, and reporting, on the job site and in the office.',
} as const;

/** Top-nav destinations (in order). `href` values are Expo Router paths. */
export const NAV_LINKS: { label: string; href: string }[] = [
  { label: 'Features', href: '/#features' },
  { label: 'How it works', href: '/#how' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Download', href: '/download' },
];

/** Hero. */
export const HERO = {
  eyebrow: 'Field-to-office, one source of truth',
  title: 'The operating system for trades.',
  subtitle:
    'Capture a lead, build the estimate, schedule the crew, pull the permit, send the invoice, and see your P&L, without re-keying a thing. Beam keeps the field and the office on the same page.',
  primaryCta: { label: 'Get started', href: '/pricing' },
  secondaryCta: { label: 'Open the web app', href: '/login' },
  note: 'One plan, everything included. iOS, Android, and web.',
} as const;

/** Trust strip under the hero. */
export const TRUST = [
  'Built for electricians, plumbers, HVAC & GC',
  'Works offline on the truck',
  'Card & ACH payments built in',
] as const;

/** Feature grid. */
export const FEATURES: { title: string; body: string }[] = [
  {
    title: 'Estimates that build themselves',
    body: 'A photo, a voice note, or an inbound message becomes a draft estimate with the customer, scope, and line items already filled in. Price it and send.',
  },
  {
    title: 'Invoices & payments',
    body: 'Turn an approved estimate into an invoice in a tap. Customers pay by card or ACH and the invoice marks itself paid. Numbers always match across the app.',
  },
  {
    title: 'Scheduling & jobs',
    body: 'Dispatch the crew, track phases, and keep punchlists tight. Everyone sees the same schedule, on the truck or at the desk.',
  },
  {
    title: 'Permits & government',
    body: 'Prepare permit packets and notices of commencement with the job data you already have. Fewer trips to the county office.',
  },
  {
    title: 'CRM & intake',
    body: 'Every call, email, and web form lands in one priority queue. Nothing falls through, and follow-up is one tap away.',
  },
  {
    title: 'Reporting your CPA will love',
    body: 'Live P&L, per-job profitability, 1099 totals, and materials forecasting. Export clean CSVs and print board-ready PDFs from the web app.',
  },
] as const;

/** "How it works" steps. */
export const STEPS: { step: string; title: string; body: string }[] = [
  { step: '01', title: 'Capture', body: 'Snap a receipt, dictate a scope, or let an inbound lead flow straight into Beam.' },
  { step: '02', title: 'Quote & schedule', body: 'Review the auto-built estimate, send it for e-signature, and put the job on the calendar.' },
  { step: '03', title: 'Do the work', body: 'Track phases and punchlists in the field. Offline-first, synced the moment you reconnect.' },
  { step: '04', title: 'Get paid & report', body: 'Invoice, collect card/ACH payments, and watch the P&L update in real time.' },
] as const;

/**
 * Pricing. The actual charge is driven by the Stripe prices configured on the backend
 * (STRIPE_PRICE_ID monthly / STRIPE_PRICE_ID_ANNUAL); the figures below are what the marketing
 * page DISPLAYS and must be kept in sync with those prices. The buyer picks a billing interval
 * ('month' | 'year') on the page and the backend maps it to the matching Stripe price — the
 * client never sends a raw price id. Edit here when a plan changes.
 */
export const PRICING = {
  /** Self-serve billing intervals shown by the Monthly/Annual toggle. */
  intervals: [
    { id: 'month', label: 'Monthly' },
    { id: 'year', label: 'Annual' },
  ],
  plans: [
    {
      id: 'crew',
      name: 'Crew',
      priceDisplay: '$49',
      unit: '/month',
      // Annual option for the Crew plan. `note` shows under the price when Annual is selected.
      annual: { priceDisplay: '$599', unit: '/year', note: 'Billed annually' },
      tagline: 'Everything Beam does, for a team of up to 5.',
      features: [
        'Up to 5 users',
        'Estimates, invoices & card/ACH payments',
        'Scheduling, jobs & punchlists',
        'Permits & government filings',
        'CRM intake & priority queue',
        'Reporting, exports & printing',
        'Mobile + web app',
      ],
      cta: 'Get started',
      featured: true,
      selfServe: true,
    },
    {
      id: 'business',
      name: 'Business',
      priceDisplay: "Let's talk",
      unit: '',
      annual: null,
      tagline: 'Unlimited users, licensed to your organization.',
      features: [
        'Unlimited users',
        'Everything in Crew',
        'Organization-wide license',
        'Subcontractor management',
        'Custom pricing catalogs & assemblies',
        'Onboarding & data import',
        'Dedicated support',
      ],
      cta: 'Contact us',
      featured: false,
      selfServe: false,
    },
  ],
  contactEmail: 'sales@getabeam.com',
  guarantee: 'Up to 5 users · cancel anytime · no setup fees',
} as const;

/** Self-serve billing interval the buyer can choose on the pricing page. */
export type BillingInterval = (typeof PRICING.intervals)[number]['id'];

export const PRICING_FAQ: { q: string; a: string }[] = [
  { q: 'What do I get for $49 a month?', a: 'Everything Beam does: estimates, invoices, card/ACH payments, scheduling, jobs, permits, government filings, CRM intake, and reporting, for up to 5 users on mobile and web.' },
  { q: 'What if I have more than 5 people?', a: 'The Business plan adds unlimited users, licensed to your organization. Contact us and we will get you set up.' },
  { q: 'What does it run on?', a: 'Beam runs on iPhone, Android, and any modern web browser. The web app is best for reporting, exporting, and printing.' },
  { q: 'Can my crew use it offline?', a: 'Yes. The mobile app is offline-first. Capture and edits sync automatically when you reconnect.' },
  { q: 'How do payments work?', a: 'Customers pay invoices by card or ACH through Stripe. Beam marks the invoice paid automatically; your invoice numbers always match.' },
  { q: 'Can I cancel?', a: 'Anytime, from the billing portal in your account. No long-term contract.' },
] as const;

/**
 * Download targets. REM-DEBT-04 — canonical store URLs (were placeholder TODOs). iOS points at the
 * real App Store listing (app id 6783591663, "Get BEAM"); Android at the Play listing for the app's
 * package id. The APK path is served by the web app.
 */
export const DOWNLOADS = {
  ios: { label: 'Download on the App Store', href: 'https://apps.apple.com/app/id6783591663' },
  android: { label: 'Get it on Google Play', href: 'https://play.google.com/store/apps/details?id=com.beammobile.app' },
  apk: { label: 'Direct Android APK', href: '/downloads/beam-latest.apk' },
  web: { label: 'Open the web app', href: '/login' },
} as const;

/** Footer link columns. */
export const FOOTER = {
  columns: [
    { title: 'Product', links: [
      { label: 'Features', href: '/#features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Download', href: '/download' },
      { label: 'Web app', href: '/login' },
    ] },
    { title: 'Company', links: [
      { label: 'About', href: '/#how' },
      { label: 'Contact sales', href: '/pricing' },
    ] },
    { title: 'Legal', links: [
      { label: 'Legal Center', href: '/legal' },
      { label: 'Terms of Service', href: '/legal/terms' },
      { label: 'Privacy Policy', href: '/legal/privacy' },
      { label: 'Refund & Return Policy', href: '/legal/refunds' },
      { label: 'Account Deletion', href: '/legal/delete-account' },
    ] },
  ],
  legal: `© ${new Date().getFullYear()} ${SITE.vendor}. Beam and the Beam mark are trademarks of ${SITE.vendor}.`,
} as const;
